import { LoadHeadersRFC } from "@/Utils/Promises";
import { ImportTypes } from "@/Utils/Constants";
import { GetWSMgr } from "@/Utils/StateLib";
import { kConnectorDataKeys } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { appstr } from "@coreapp/utils/appstr";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { Globals } from "@coreapp/CoreApp/Globals";
import { getReadableStringFromDateAndTime } from "@coreapp/utils/getReadableStringFromDateAndTime";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import {
  kAccountEnterprise,
  kPositionID,
  kParentPositionID,
  kPersonID,
  kSupervisorID,
  kEmployeePositionID
} from "@coreapp/constants";
import { getHoverTip } from "@coreapp/utils/getHoverTip";
import { SynthesizePositions } from "@coreapp/utils/SynthesizePositions";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import {
  GetAllOptionalFields,
  GetPromotedPositionEmployeeFieldGroups,
  GetRequiredFields
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/Utils";
//================================================
// Connector configuration and mapping validation
// Connectors that require rfc to load headers and the required configuration section keys
export const PopulatePositionFieldsFromMapping = (chartConfig, mappingConfig) => {
  if (!IsPositionChart(chartConfig) || !mappingConfig) {
    return { holderFields: [], positionFields: [] };
  }

  const holderFields = new Set();
  const positionFields = new Set();

  const positionFieldIds = [
    "PositionID",
    "Parent PositionID",
    "Position Location",
    "Position Title",
    "Position Division",
    "Desired Holders",
    "Position Budgeted Salary",
    "Position Cost Center",
    "Position Cost Band Minimum",
    "Position Cost Band Midpoint",
    "Position Cost Band Maximum"
  ];

  const employeeFieldIds = [
    "EmployeeID",
    "Employee SupervisorID",
    "Employee PositionID",
    "Employee Location",
    "Name",
    "Title",
    "First Name",
    "Preferred First",
    "Last Name",
    "Preferred Last",
    "aTitle",
    "Division",
    "Employee Salary",
    "Employee Pay",
    "Employee Cost Center"
  ];

  // Populate fields based on mapping
  Object.entries(mappingConfig).forEach(([fieldId, mappedValue]) => {
    if (mappedValue) {
      if (positionFieldIds.includes(fieldId)) {
        positionFields.add(mappedValue);
      } else if (employeeFieldIds.includes(fieldId)) {
        holderFields.add(mappedValue);
      }
    }
  });

  return {
    holderFields: Array.from(holderFields),
    positionFields: Array.from(positionFields)
  };
};

export const LoadCurrentChartMapping = (curChartObj, dataSourceType = ImportTypes.import) => {
  const newChartConfig = JSON.parse(JSON.stringify(curChartObj));
  const dataSource = newChartConfig[dataSourceType]["source"];
  let changedSections = [];
  let mappingConfig = GetMapping(dataSource, newChartConfig);

  let needRFC = mappingConfig === false;
  if (mappingConfig) {
    newChartConfig["fields"] = mappingConfig;
    changedSections.push("fields");

    const posConfig = new PositionConfig(newChartConfig);
    if (posConfig.isPositionChart() && !needRFC) {
      const { holderFields, positionFields } = PopulatePositionFieldsFromMapping(
        newChartConfig,
        mappingConfig
      );
      newChartConfig["holderFields"] = holderFields;
      newChartConfig["positionFields"] = positionFields;
      changedSections.push("holderFields", "positionFields");
    }
  }
  return { newChartConfig, needRFC, changedSections };
};

export const LoadHeadersHelper = async (type, index, keys, chartConfig, config) => {
  const options = {};
  options.type = type;
  options.sourceIndex = index;
  options.connectionOptions = config;
  options.keys = keys;

  try {
    const { newChartConfig, needRFC, changedSections } = LoadCurrentChartMapping(chartConfig, type);
    if (needRFC) {
      const { response } = await LoadHeadersRFC(chartConfig, options);

      if (!response["success"]) return { success: false };

      const { newChartObj } = AttachLoadedHeadersToChart(
        response,
        newChartConfig,
        type,
        index,
        keys
      );

      return {
        headers: response.headers,
        success: response.success
      };
    } else {
      const headers = new Set();
      const fields = newChartConfig.fields || {};

      Object.values(fields).forEach(value => {
        if (value && value !== null) {
          headers.add(value);
        }
      });

      const headersArray = Array.from(headers);
      newChartConfig["headers"] = headersArray;

      return {
        newChartConfig,
        headers: headersArray,
        success: true,
        changedSections: [...changedSections, "headers"]
      };
    }
  } catch (err) {
    return { success: false };
  }
};

export const CanAddAdditionalDataSource = chartConfig => {
  const append = chartConfig.append;
  const merge = chartConfig.merge;
  const currentEdition = Globals._accountInfo.GetOC5Edition();
  const isEnterprise = currentEdition === kAccountEnterprise;
  const maxExtra = isEnterprise ? 10 : 2;
  const extraDataLength = (append?.length ?? 0) + (merge?.length ?? 0);
  const isOver = extraDataLength >= maxExtra;
  if (isOver === false) {
    return true;
  }
  const message = isEnterprise ? appstr("Enterprise source limit") : getHoverTip("DataAggPaywall");
  const wsMgr = GetWSMgr();
  const alert = new QAlert();
  alert.InitAlert(appstr("Over source limit"), message, null, wsMgr);
  return false;
};

export const GetConnectionConfigured = curChartObj => {
  var isConfigured = false;
  if (!curChartObj) return false;
  var importObj = curChartObj["import"];
  var dataSource = importObj["source"];
  var keyCheckList = kConnectorDataKeys[dataSource];

  if (dataSource == "") return false;

  // don't care about checking for configuration here if headers don't need to be loaded from the backend
  const { needRFC } = LoadCurrentChartMapping(curChartObj);
  if (!needRFC) return true;

  if (keyCheckList == null) return false;

  if (keyCheckList.length === 0) return true; //This data source doesn't require any config keys

  var curValueList = [];
  var curKeyList = Object.keys(importObj);

  // get relevant values for data source
  curKeyList.forEach(function (item) {
    if (keyCheckList.indexOf(item) > -1) curValueList.push(importObj[item]);
  });

  //check for empty, null, or undefined values
  isConfigured =
    curValueList.length !== 0 &&
    curValueList.indexOf("") === -1 &&
    curValueList.indexOf(appstr("notset")) === -1 &&
    curValueList.indexOf(undefined) === -1 &&
    curValueList.indexOf(null) === -1;

  return isConfigured;
};

export const ValidateMapping = curChartObj => {
  if (!curChartObj) return false;

  const requiredKeyList = GetRequiredFields(curChartObj);
  const optionalKeyList = GetAllOptionalFields(curChartObj);
  const fieldObj = curChartObj["fields"];
  const headersList = curChartObj["headers"];
  const isPosChart = IsPositionChart(curChartObj);

  if (!fieldObj) return false;

  const curFieldList = Object.keys(fieldObj);
  for (const reqKey of requiredKeyList) {
    if (curFieldList.indexOf(reqKey) === -1) return false;
  }

  const requiredFieldMappings = [];
  curFieldList.forEach(item => {
    if (Object.keys(requiredKeyList).indexOf(item) > -1) {
      requiredFieldMappings.push({
        key: item,
        value: fieldObj[item]
      });
    }
  });

  //check for empty, null, or undefined values in required field mappings
  const emptyValues = ["", appstr("notset"), undefined, null];
  const hasEmptyRequiredField = requiredFieldMappings.some(mapping =>
    emptyValues.includes(mapping.value)
  );

  if (hasEmptyRequiredField) return false;

  let internallyGeneratedFields = [];
  if (isPosChart && SynthesizePositions(curChartObj)) {
    const positionFields = {
      "Employee PositionID": kEmployeePositionID,
      PositionID: kPositionID,
      "Parent PositionID": kParentPositionID
    };
    Object.assign(fieldObj, positionFields);
    internallyGeneratedFields = [kPositionID, kEmployeePositionID, kParentPositionID];
  }

  //check if values exist in data
  // TODO: does flat file import need to add diagnostics here? we have total control over the mapping...
  if (headersList) {
    for (const mapping of requiredFieldMappings) {
      const valueExists =
        headersList.indexOf(mapping.value) > -1 ||
        internallyGeneratedFields.indexOf(mapping.value) > -1;
      if (!valueExists) return false;
    }
  }

  // if it passes fields existing in data, check for duplicates
  if (isPosChart) {
    // Get position and employee field groups
    const { posFieldGroup, empFieldGroup } = GetPromotedPositionEmployeeFieldGroups();

    const positionFieldKeys = Object.keys(posFieldGroup);
    const employeeFieldKeys = Object.keys(empFieldGroup);

    const positionMappedValues = [];
    const employeeMappedValues = [];

    requiredFieldMappings.forEach(mapping => {
      if (positionFieldKeys.includes(mapping.key)) {
        positionMappedValues.push(mapping.value);
      } else if (employeeFieldKeys.includes(mapping.key)) {
        employeeMappedValues.push(mapping.value);
      }
    });

    const uniquePositionValues = positionMappedValues.filter(
      (value, index, self) =>
        internallyGeneratedFields.includes(value) || self.indexOf(value) === index
    );

    const uniqueEmployeeValues = employeeMappedValues.filter(
      (value, index, self) =>
        internallyGeneratedFields.includes(value) || self.indexOf(value) === index
    );

    if (
      uniquePositionValues.length !== positionMappedValues.length ||
      uniqueEmployeeValues.length !== employeeMappedValues.length
    ) {
      return false;
    }
  } else {
    // Only consider dup if its not internally generated field
    const allMappedValues = requiredFieldMappings.map(mapping => mapping.value);
    const uniqueValues = allMappedValues.filter(
      (value, index, self) =>
        internallyGeneratedFields.includes(value) || self.indexOf(value) === index
    );

    if (uniqueValues.length !== allMappedValues.length) {
      return false;
    }
  }

  //==============================================================================
  // Next, we check optional field mappings

  const optionalKeyListWithAux = [...optionalKeyList, "AuxSuperID"];

  //check for empty, null, or undefined values
  optionalKeyListWithAux.forEach(fieldID => {
    if (fieldID === "AuxSuperID" && !fieldObj[fieldID]) {
      return;
    }

    if (!fieldObj[fieldID]) {
      // here we auto assign mappings for the optional section using the valid required mappings if field ID doesnt exist
      switch (fieldID) {
        case "Location":
        case "aTitle":
          if (headersList == null || (headersList && headersList.indexOf(fieldObj["Title"]) > -1))
            fieldObj[fieldID] = fieldObj["Title"];
          break;
        case "First Name":
        case "Last Name":
          if (headersList == null || (headersList && headersList.indexOf(fieldObj["Name"]) > -1))
            fieldObj[fieldID] = fieldObj["Name"];
          break;
        case "Preferred First":
          if (
            headersList == null ||
            (headersList && headersList.indexOf(fieldObj["First Name"]) > -1)
          )
            fieldObj[fieldID] = fieldObj["First Name"];
          break;
        case "Preferred Last":
          if (
            headersList == null ||
            (headersList && headersList.indexOf(fieldObj["First Name"]) > -1)
          )
            fieldObj[fieldID] = fieldObj["First Name"];
          break;
      }
    }
  });

  return true;
};
const kRequireLoadHeaders = [
  "activedir",
  "AzureAD",
  "Bamboo",
  "BreatheHR",
  "Dayforce",
  "CSV",
  "Igloo",
  "ODBC",
  "Oracle",
  "Paychex",
  "Paycom",
  "Paycor",
  "paylocity",
  "paylocity_nextgen",
  "PaySpace",
  "SAP",
  "XLS",
  "SFTP",
  "Sage",
  "Salesforce",
  "Signify",
  "SuccessFactors",
  "TriNet",
  "UKG Ready",
  "Ultipro",
  "Workday",
  "Manual"
];

// Helper function to get common employee field mappings for a connector across both position and employee charts
export const GetCommonEmployeeFieldMappings = dataSource => {
  switch (dataSource) {
    case "ADP":
    case "ADP Canada":
    case "Vantage":
    case "ADP Enterprise":
    case "ADP Lifion":
    case "ADP NG HCM":
    case "WFN Next Generation":
    case "UnicornHRO":
      return {
        Name: "Name",
        Title: "Title",
        aTitle: "Title",
        "First Name": "First Name",
        "Preferred First": "First Name",
        "Last Name": "Last Name",
        "Preferred Last": "Last Name",
        Division: "Division",
        Location: "Location"
      };
    case "Dayforce":
      return {
        Name: "Name",
        Title: "Title",
        aTitle: "Title",
        "First Name": "First Name",
        "Preferred First": "First Name",
        "Last Name": "Last Name",
        "Preferred Last": "Last Name",
        Division: "Division",
        Location: "Location"
      };
    case "ADP Europe":
      return {
        Name: "Name",
        Title: "Title",
        aTitle: "Title",
        "First Name": "First Name",
        "Preferred First": "First Name",
        "Last Name": "Last Name",
        "Preferred Last": "Last Name",
        Division: "Division",
        Location: "Work Location"
      };
    case "AzureAD":
      return {
        Name: "displayName",
        Title: "jobTitle",
        aTitle: "jobTitle",
        "First Name": "givenName",
        "Preferred First": "givenName",
        "Last Name": "surname",
        "Preferred Last": "surname",
        Division: "officeLocation",
        Location: "officeLocation"
      };
    case "Bamboo":
      return {
        Name: "Display Name",
        Title: "Job Title",
        aTitle: "Job Title",
        "First Name": "First Name",
        "Preferred First": "First Name",
        "Last Name": "Last Name",
        "Preferred Last": "Last Name",
        Division: "Division",
        Location: "Location"
      };
    case "Google Directory":
      return {
        Name: "fullName",
        Title: "jobTitle",
        aTitle: "jobTitle",
        "First Name": "givenName",
        "Preferred First": "givenName",
        "Last Name": "familyName",
        "Preferred Last": "familyName",
        Division: "department",
        Location: "locationType"
      };
    case "UKG Ready":
      return {
        Name: "full_name",
        Title: "title",
        aTitle: "title",
        "First Name": "first_name",
        "Preferred First": "first_name",
        "Last Name": "last_name",
        "Preferred Last": "last_name",
        Division: "Division_0",
        Location: "Location_0"
      };
    case "BreatheHR":
      return {
        Name: "Name",
        Title: "job_title",
        aTitle: "job_title",
        "First Name": "first_name",
        "Preferred First": "known_as",
        "Last Name": "last_name",
        "Preferred Last": "last_name",
        Division: "division_name",
        Location: "location_name"
      };
    case "OrangeHRM":
      return {
        Name: "Full Name",
        Title: "Job Title",
        aTitle: "Job Title Code",
        "First Name": "First Name",
        "Preferred First": "First Name",
        "Last Name": "Last Name",
        "Preferred Last": "Last Name",
        Division: "Job Title",
        Location: "Location"
      };
    case "PeopleHR":
      return {
        Name: "Name",
        Title: "JobRole",
        aTitle: "JobRole",
        "First Name": "FirstName",
        "Preferred First": "FirstName",
        "Last Name": "LastName",
        "Preferred Last": "LastName",
        Division: "Department",
        Location: "Location"
      };
    case "SubscribeHR":
      return {
        Name: "name",
        Title: "jobName",
        aTitle: "jobTitle",
        "First Name": "FirstName",
        "Preferred First": "FirstName",
        "Last Name": "Surname",
        "Preferred Last": "Surname",
        Division: "Department",
        Location: "Location"
      };
    case "Lucca":
      return {
        Name: "name",
        Title: "jobTitle",
        aTitle: "jobTitle",
        "First Name": "firstName",
        "Preferred First": "firstName",
        "Last Name": "lastName",
        "Preferred Last": "lastName",
        Division: "departmentName",
        Location: "departmentID"
      };
    case "Sage 300 ZA":
      return {
        Name: "full Name",
        Title: "job Title",
        aTitle: "job Title",
        "First Name": "first Name",
        "Preferred First": "first Name",
        "Last Name": "last Name",
        "Preferred Last": "last Name",
        Division: "department",
        Location: "branch"
      };
    case "SAP XML":
    case "SAP RFC":
      return {
        Name: "Name",
        Title: "Title",
        aTitle: "Relationship",
        "First Name": "Name",
        "Preferred First": "Name",
        "Last Name": "Name",
        "Preferred Last": "Name",
        Division: "OrgUnitID",
        Location: "OrgUnitName"
      };
    case "Dynchart":
      return {
        Name: "Name",
        Title: "Title"
      };
    case "PaySpace":
      return {
        Name: "Name",
        Title: "JobTitle",
        aTitle: "CostCenter",
        "First Name": "FirstName",
        "Preferred First": "FirstName",
        "Last Name": "LastName",
        "Preferred Last": "LastName",
        Division: "Department",
        Location: "FunctionalArea"
      };
    case "Namely":
      return {
        Name: "Full Name",
        Title: "Job Title",
        aTitle: "Job Level Code",
        "First Name": "First Name",
        "Preferred First": "Preferred Name",
        "Last Name": "Last Name",
        "Preferred Last": "Preferred Name",
        Division: "Department",
        Location: "Home City"
      };
    case "UKG":
      return {
        Name: "fullName",
        Title: "jobDescription",
        aTitle: "jobTitle",
        "First Name": "firstName",
        "Preferred First": "preferredName",
        "Last Name": "lastName",
        "Preferred Last": "lastName",
        Division: "orgLevel2Code",
        Location: "primaryWorkLocationCode"
      };
    case "Cornerstone":
      return {
        Name: "fullName",
        Title: "Position",
        aTitle: "Position",
        "First Name": "firstName",
        "Preferred First": "firstName",
        "Last Name": "lastName",
        "Preferred Last": "lastName",
        Division: "Department",
        Location: "addressCountry"
      };
    case "Signify":
      return {
        Name: "Name",
        Title: "JobTitle",
        aTitle: "JobTitle",
        "First Name": "FirstName",
        "Preferred First": "FirstName",
        "Last Name": "Surname",
        "Preferred Last": "Surname",
        Division: "OrgLevel4Description",
        Location: "Location"
      };
    case "Paychex":
      return {
        Name: "fullName",
        Title: "jobTitle",
        aTitle: "jobTitle",
        "First Name": "givenName",
        "Preferred First": "preferredName",
        "Last Name": "familyName",
        "Preferred Last": "familyName",
        Division: "organizationNumber",
        Location: "organizationName"
      };
    case "Paycom":
      return {
        Name: "eename",
        Title: "business_title",
        aTitle: "business_title",
        "First Name": "firstname",
        "Preferred First": "firstname",
        "Last Name": "lastname",
        "Preferred Last": "lastname",
        Location: "city",
        Division: "deptdesc"
      };
    case "TriNet":
      return {
        Name: "fullName",
        Title: "businessTitle",
        aTitle: "businessTitle",
        "First Name": "First Name",
        "Preferred First": "First Name",
        "Last Name": "Last Name",
        "Preferred Last": "Last Name",
        Division: "deptName",
        Location: "locationName"
      };
    case "Thrive TRM":
      return {
        Name: "ascii_name",
        Title: "primary_title",
        aTitle: "primary_title",
        "First Name": "first_name",
        "Preferred First": "first_name",
        "Last Name": "last_name",
        "Preferred Last": "last_name",
        Division: "primary_company_name",
        Location: "city"
      };
    case "paylocity":
    case "paylocity_nextgen":
      return {
        Name: "firstName",
        Title: "departmentPosition_jobTitle",
        aTitle: "departmentPosition_jobTitle",
        "First Name": "firstName",
        "Preferred First": "firstName",
        "Last Name": "lastName",
        "Preferred Last": "lastName",
        Division: "companyName",
        Location: "homeAddress_city"
      };
    case "isolved":
      return {
        Name: "employee_name",
        Title: "jobTitle",
        aTitle: "jobTitle",
        "First Name": "nameAddress_firstName",
        "Preferred First": "nameAddress_firstName",
        "Last Name": "nameAddress_lastName",
        "Preferred Last": "nameAddress_lastName",
        Division: "payGroup",
        Location: "nameAddress_city"
      };
    case "Jemini":
      return {
        Name: "employeeName",
        Title: "positionTitle",
        aTitle: "positionTitle",
        "First Name": "employeeFirstName",
        "Preferred First": "employeeFirstName",
        "Last Name": "employeeLastName",
        "Preferred Last": "employeeLastName",
        Division: "employeeOccupancyOrganisationComponent",
        Location: "location"
      };
    default:
      return {};
  }
};

export const GetOrganicPositionFieldMappings = dataSource => {
  switch (dataSource) {
    case "ADP":
    case "ADP Canada":
    case "Vantage":
    case "ADP Enterprise":
    case "ADP Lifion":
    case "ADP NG HCM":
    case "WFN Next Generation":
    case "UnicornHRO":
      return {};
    case "Dayforce":
      return {};
    case "ADP Europe":
      return {};
    case "AzureAD":
      return {};
    case "Bamboo":
      return {};
    case "Google Directory":
      return {};
    case "UKG Ready":
      return {};
    case "BreatheHR":
      return {};
    case "OrangeHRM":
      return {};
    case "PeopleHR":
      return {};
    case "SubscribeHR":
      return {};
    case "Lucca":
      return {};
    case "Sage 300 ZA":
      return {};
    case "SAP XML":
    case "SAP RFC":
      return {};
    case "Dynchart":
      return {};
    case "PaySpace":
      return {};
    case "Namely":
      return {};
    case "UKG":
      return {};
    case "Cornerstone":
      return {};
    case "Signify":
      return {};
    case "Paychex":
      return {};
    case "Paycom":
      return {
        PositionID: "positionSeatNumber",
        "Parent PositionID": "reportsToPositionSeat",
        "Position Title": "positionTitle",
        "Employee PositionID": "PositionID",
        EmployeeID: "employee_code"
      };
    case "TriNet":
      return {};
    case "Thrive TRM":
      return {};
    case "paylocity":
    case "paylocity_nextgen":
      return {};
    case "isolved":
      return {};
    case "Jemini":
      return {};
    default:
      return {};
  }
};

export const GetPositionMapping = (dataSource, curChartObj = null, isSynth) => {
  let mappingConfig = {
    // auto generated fields
    PositionID: "PositionID",
    "Parent PositionID": "Parent PositionID",
    "Employee PositionID": "Employee PositionID",

    // Employee hierarchy fields
    EmployeeID: null,
    "Employee SupervisorID": null,

    // Employee fields
    Name: null,
    Title: null,
    "First Name": null,
    "Preferred First": null,
    "Last Name": null,
    "Preferred Last": null,
    aTitle: null,
    "Employee Location": null,

    // Position fields - initialize once, will be updated if we have specific mappings
    "Position Title": null,
    "Position Location": null,
    "Position Division": null,
    "Desired Holders": null,

    // Budget fields
    "Employee Salary": null,
    "Employee Pay": null,
    "Employee Cost Center": null,
    "Position Budgeted Salary": null,
    "Position Cost Center": null,
    "Position Cost Band Minimum": null,
    "Position Cost Band Midpoint": null,
    "Position Cost Band Maximum": null
  };

  // Get common employee field mappings
  const commonMappings = GetCommonEmployeeFieldMappings(dataSource);
  const positionMappings = isSynth ? {} : GetOrganicPositionFieldMappings(dataSource);

  switch (dataSource) {
    case "ADP":
    case "ADP Canada":
    case "Vantage":
    case "ADP Enterprise":
    case "ADP Lifion":
    case "ADP NG HCM":
    case "WFN Next Generation":
    case "UnicornHRO":
      Object.assign(mappingConfig, {
        EmployeeID: "PersonID",
        "Employee SupervisorID": "SupervisorID",
        ...commonMappings
      });
      break;
    case "Dayforce":
      Object.assign(mappingConfig, {
        EmployeeID: "XRefCode",
        "Employee SupervisorID": "ManagerXRefCode",
        ...commonMappings
      });
      break;
    case "ADP Europe":
      Object.assign(mappingConfig, {
        EmployeeID: "PersonID",
        "Employee SupervisorID": "SupervisorID",
        ...commonMappings
      });
      break;
    case "AzureAD":
      Object.assign(mappingConfig, {
        EmployeeID: "eId",
        "Employee SupervisorID": "mgrId",
        ...commonMappings
      });
      break;
    case "Bamboo":
      Object.assign(mappingConfig, {
        EmployeeID: "EEID",
        "Employee SupervisorID": "Supervisor EID",
        ...commonMappings
      });
      break;
    case "Google Directory":
      Object.assign(mappingConfig, {
        EmployeeID: "primaryEmail",
        "Employee SupervisorID": "managerRelations",
        ...commonMappings
      });
      break;
    case "UKG Ready":
      Object.assign(mappingConfig, {
        EmployeeID: "empID",
        "Employee SupervisorID": "managersID",
        ...commonMappings
      });
      break;
    case "BreatheHR":
      Object.assign(mappingConfig, {
        EmployeeID: "PersonID",
        "Employee SupervisorID": "line_manager_id",
        ...commonMappings
      });
      break;
    case "OrangeHRM":
      Object.assign(mappingConfig, {
        EmployeeID: "Emp Number",
        "Employee SupervisorID": "Supervisor Id",
        ...commonMappings
      });
      break;
    case "PeopleHR":
      Object.assign(mappingConfig, {
        EmployeeID: "EmployeeId",
        "Employee SupervisorID": "ReportsToEmployeeId",
        ...commonMappings
      });
      break;
    case "SubscribeHR":
      Object.assign(mappingConfig, {
        EmployeeID: "eId",
        "Employee SupervisorID": "mgrId",
        ...commonMappings
      });
      break;
    case "Lucca":
      Object.assign(mappingConfig, {
        EmployeeID: "eID",
        "Employee SupervisorID": "managerID",
        ...commonMappings
      });
      break;
    case "Sage 300 ZA":
      Object.assign(mappingConfig, {
        EmployeeID: "eID",
        "Employee SupervisorID": "reports To",
        ...commonMappings
      });
      break;
    case "SAP XML":
    case "SAP RFC":
      Object.assign(mappingConfig, {
        EmployeeID: "PositionID",
        "Employee SupervisorID": "SupervisorID",
        ...commonMappings
      });
      if (curChartObj && curChartObj["import"]["SAPOption"] > 0) {
        mappingConfig["EmployeeID"] = "UniqueID";
      }
      break;
    case "Dynchart":
      Object.assign(mappingConfig, {
        EmployeeID: "Position ID",
        "Employee SupervisorID": "Future Manager Position ID",
        ...commonMappings
      });
      break;
    case "PaySpace":
      Object.assign(mappingConfig, {
        EmployeeID: "EmpNo",
        "Employee SupervisorID": "ManagerEmpNo",
        ...commonMappings
      });
      if (curChartObj) {
        var requestType = curChartObj["import"]["psreqtype"];
        if (typeof requestType == "string" && requestType.toUpperCase() == "JOBDIR") {
          mappingConfig["EmployeeID"] = "JobNumber";
          mappingConfig["Employee SupervisorID"] = "ManagerJobNo";
        }
      }
      break;
    case "Namely":
      Object.assign(mappingConfig, {
        EmployeeID: "PersonID",
        "Employee SupervisorID": "SupervisorID",
        ...commonMappings
      });
      break;
    case "UKG":
      Object.assign(mappingConfig, {
        EmployeeID: "employeeID",
        "Employee SupervisorID": "supervisorID",
        ...commonMappings
      });
      break;
    case "Cornerstone":
      Object.assign(mappingConfig, {
        EmployeeID: "empId",
        "Employee SupervisorID": "managerId",
        ...commonMappings
      });
      break;
    case "Signify":
      Object.assign(mappingConfig, {
        EmployeeID: "EmployeeNumber",
        "Employee SupervisorID": "LineManagerEmployeeNumber",
        ...commonMappings
      });
      break;
    case "Paychex":
      Object.assign(mappingConfig, {
        EmployeeID: "workerId",
        "Employee SupervisorID": "supervisorWorkerId",
        ...commonMappings
      });
      break;
    case "Paycom":
      Object.assign(mappingConfig, {
        EmployeeID: "eecode",
        "Employee SupervisorID": "supervisor_primary_code",
        ...positionMappings,
        ...commonMappings
      });
      break;
    case "TriNet":
      Object.assign(mappingConfig, {
        EmployeeID: "employeeId",
        "Employee SupervisorID": "supervisorId",
        ...commonMappings
      });
      break;
    case "Thrive TRM":
      Object.assign(mappingConfig, {
        EmployeeID: "contactId",
        "Employee SupervisorID": "reports_to_Id",
        ...commonMappings
      });
      break;
    case "paylocity":
    case "paylocity_nextgen":
      Object.assign(mappingConfig, {
        EmployeeID: "employeeId",
        "Employee SupervisorID": "departmentPosition_supervisorEmployeeId",
        ...commonMappings
      });
      break;
    case "isolved":
      Object.assign(mappingConfig, {
        EmployeeID: "employee_id",
        "Employee SupervisorID": "assignedManager_id",
        ...commonMappings
      });
      break;
    case "Jemini":
      Object.assign(mappingConfig, {
        EmployeeID: "employeeNumber",
        "Employee SupervisorID": "managerNumber",
        ...commonMappings
      });
      break;
    default:
      return false;
  }

  // Copy common fields to their position chart field id equivalents
  if (commonMappings.Location) {
    mappingConfig["Employee Location"] = commonMappings.Location;
  }
  if (commonMappings.Division) {
    mappingConfig["Position Division"] = commonMappings.Division;
  }
  if (commonMappings.Title) {
    mappingConfig["Position Title"] = commonMappings.Title;
  }

  // Remove original keys that aren't valid field IDs for position charts
  delete mappingConfig.Location;
  delete mappingConfig.Division;

  return mappingConfig;
};

export const GetEmployeeMapping = (dataSource, curChartObj = null) => {
  const commonMappings = GetCommonEmployeeFieldMappings(dataSource);

  let mappingConfig = {
    PersonID: null,
    SupervisorID: null,
    ...commonMappings
  };

  switch (dataSource) {
    case "ADP":
    case "ADP Canada":
    case "Vantage":
    case "ADP Enterprise":
    case "ADP Lifion":
    case "ADP NG HCM":
    case "WFN Next Generation":
    case "UnicornHRO":
      Object.assign(mappingConfig, {
        PersonID: "PersonID",
        SupervisorID: "SupervisorID"
      });
      break;
    case "Dayforce":
      Object.assign(mappingConfig, {
        PersonID: "XRefCode",
        SupervisorID: "ManagerXRefCode"
      });
      break;
    case "ADP Europe":
      Object.assign(mappingConfig, {
        PersonID: "PersonID",
        SupervisorID: "SupervisorID"
      });
      break;
    case "AzureAD":
      Object.assign(mappingConfig, {
        PersonID: "eId",
        SupervisorID: "mgrId"
      });
      break;
    case "Bamboo":
      Object.assign(mappingConfig, {
        PersonID: "EEID",
        SupervisorID: "Supervisor EID"
      });
      break;
    case "BreatheHR":
      Object.assign(mappingConfig, {
        PersonID: "PersonID",
        SupervisorID: "line_manager_id"
      });
      break;
    case "OrangeHRM":
      Object.assign(mappingConfig, {
        PersonID: "Emp Number",
        SupervisorID: "Supervisor Id"
      });
      break;
    case "PeopleHR":
      Object.assign(mappingConfig, {
        PersonID: "EmployeeId",
        SupervisorID: "ReportsToEmployeeId"
      });
      break;
    case "SubscribeHR":
      Object.assign(mappingConfig, {
        PersonID: "eId",
        SupervisorID: "mgrId"
      });
      break;
    case "Lucca":
      Object.assign(mappingConfig, {
        PersonID: "eID",
        SupervisorID: "managerID"
      });
      break;
    case "Sage 300 ZA":
      Object.assign(mappingConfig, {
        PersonID: "eID",
        SupervisorID: "reports To"
      });
      break;
    case "SAP XML":
    case "SAP RFC":
      Object.assign(mappingConfig, {
        PersonID: "PositionID",
        SupervisorID: "SupervisorID"
      });
      if (curChartObj && curChartObj["import"]["SAPOption"] > 0) {
        mappingConfig["PersonID"] = "UniqueID";
      }
      break;
    case "Dynchart":
      Object.assign(mappingConfig, {
        PersonID: "Position ID",
        SupervisorID: "Future Manager Position ID"
      });
      break;
    case "PaySpace":
      Object.assign(mappingConfig, {
        PersonID: "EmpNo",
        SupervisorID: "ManagerEmpNo"
      });
      if (curChartObj) {
        var requestType = curChartObj["import"]["psreqtype"];
        if (typeof requestType == "string" && requestType.toUpperCase() == "JOBDIR") {
          mappingConfig["PersonID"] = "JobNumber";
          mappingConfig["SupervisorID"] = "ManagerJobNo";
        }
      }
      break;
    case "Namely":
      Object.assign(mappingConfig, {
        PersonID: "PersonID",
        SupervisorID: "SupervisorID"
      });
      break;
    case "UKG":
      Object.assign(mappingConfig, {
        PersonID: "employeeID",
        SupervisorID: "supervisorID"
      });
      break;
    case "UKG Ready":
      Object.assign(mappingConfig, {
        PersonID: "empID",
        SupervisorID: "managersID"
      });
      break;
    case "Cornerstone":
      Object.assign(mappingConfig, {
        PersonID: "empId",
        SupervisorID: "managerId"
      });
      break;
    case "Signify":
      Object.assign(mappingConfig, {
        PersonID: "EmployeeNumber",
        SupervisorID: "LineManagerEmployeeNumber"
      });
      break;
    case "Paychex":
      Object.assign(mappingConfig, {
        PersonID: "workerId",
        SupervisorID: "supervisorWorkerId"
      });
      break;
    case "Paycom":
      Object.assign(mappingConfig, {
        PersonID: "eecode",
        SupervisorID: "supervisor_primary_code"
      });
      break;
    case "Google Directory":
      Object.assign(mappingConfig, {
        PersonID: "primaryEmail",
        SupervisorID: "managerRelations"
      });
      break;
    case "TriNet":
      Object.assign(mappingConfig, {
        PersonID: "employeeId",
        SupervisorID: "supervisorId"
      });
      break;
    case "Thrive TRM":
      Object.assign(mappingConfig, {
        PersonID: "contactId",
        SupervisorID: "reports_to_Id"
      });
      break;
    case "paylocity":
    case "paylocity_nextgen":
      Object.assign(mappingConfig, {
        PersonID: "employeeId",
        SupervisorID: "departmentPosition_supervisorEmployeeId"
      });
      break;
    case "isolved":
      Object.assign(mappingConfig, {
        PersonID: "employee_id",
        SupervisorID: "assignedManager_id"
      });
      break;
    case "Jemini":
      Object.assign(mappingConfig, {
        PersonID: "employeeNumber",
        SupervisorID: "managerNumber"
      });
      break;
    default:
      return false;
  }

  return mappingConfig;
};

export const GetMapping = (dataSource, curChartObj) => {
  const posConfig = new PositionConfig(curChartObj);
  if (posConfig.isPositionChart()) {
    const positionFieldMapping = GetPositionMapping(
      dataSource,
      curChartObj,
      posConfig.isPositionSynth()
    );
    if (positionFieldMapping) {
      return positionFieldMapping;
    }
  }
  return GetEmployeeMapping(dataSource, curChartObj);
};

export const AttachLoadedHeadersToChart = (r, curChartObj, type, index, additionalFields) => {
  var headers = r["headers"];
  var mapping = r["mapping"];
  var newChartObj = JSON.parse(JSON.stringify(curChartObj));
  var changedSections = [];
  var isPositionChart = IsPositionChart(newChartObj); // In theory, append/merge files can be positions OR holders data OR both. At this time, it's only holders data.
  switch (type.toLowerCase()) {
    case "import":
      changedSections = ["headers", "fields", "import"];
      newChartObj["headers"] = headers;
      newChartObj["fields"] = mapping;
      newChartObj["import"]["headers"] = headers;
      if (isPositionChart) {
        newChartObj["holderFields"] = r["holderFields"] || [];
        newChartObj["positionFields"] = r["positionFields"] || [];
        changedSections = changedSections.concat(["holderFields", "positionFields"]);
      }
      break;
    case "merge":
      var recordField = GetHierarchyKey(newChartObj, kPersonID, newChartObj["fields"] || {});
      var mergeField = GetHierarchyKey(newChartObj, kPersonID, mapping);
      PopulateHeadersAndMap(
        newChartObj,
        "Merge",
        index,
        headers,
        recordField,
        mergeField,
        additionalFields
      );
      changedSections.push("merge");
      break;
    case "append":
      var mappingPassThrough = isPositionChart
        ? { "Employee PositionID": mapping["Employee PositionID"] }
        : null;
      var personID = GetHierarchyKey(newChartObj, kPersonID, mapping);
      var supervisorID = GetHierarchyKey(newChartObj, kSupervisorID, mapping);
      PopulateHeadersAndMap(
        newChartObj,
        "Append",
        index,
        headers,
        personID,
        supervisorID,
        additionalFields,
        mappingPassThrough
      );
      changedSections.push("append");
      break;
  }
  return { newChartObj, changedSections };
};

export const GetHierarchyKey = function (chartMetadata, keyParam, fieldDict) {
  // TO-DO: POS MGMT - Update this fn. when ready.
  var isPositions = false; // IsPositionChart(chartMetadata);
  var hierarchyMapping = {
    kPersonID: kPositionID,
    kSupervisorID: kParentPositionID
  };
  var baseKey = isPositions ? hierarchyMapping[keyParam] : keyParam;
  return fieldDict ? fieldDict[baseKey] : baseKey;
};

export const PopulateHeadersAndMap = (
  chart,
  mode,
  index,
  headers,
  fieldAVal,
  fieldBVal,
  additionalFields,
  mapping
) => {
  // Two modes, mapping = null or mapping is an object.
  var subMode = mode.toLowerCase();
  chart[subMode][index]["headers"] = headers;
  chart[subMode][index]["map"] = chart[subMode][index]["map"] || {}; // It might be worth-while to always reset this to not have a bloated map.
  if (mapping) {
    for (var key of Object.keys(mapping)) chart[subMode][index]["map"][key] = mapping[key];
  } else {
    var fieldAKey = subMode == "append" ? GetHierarchyKey(chart, kPersonID) : "recordField";
    var fieldBKey = subMode == "append" ? GetHierarchyKey(chart, kSupervisorID) : "mergeField";
    chart[subMode][index]["map"][fieldAKey] = fieldAVal;
    chart[subMode][index]["map"][fieldBKey] = fieldBVal;
  }
  PopulateEmail(chart, subMode, index, headers, additionalFields); // For ATS.
};

export const PopulateEmail = (chart, mode, index, headers, key) => {
  if (headers == null || key == null) return;
  for (var header of headers)
    if (header && header.toLowerCase().includes("email")) {
      chart[mode][index][key] = header;
      return;
    }
};

export const GetChartRefreshInfo = (chartConfig, dateFormat, timeFormat) => {
  var refreshDate = chartConfig["Timestamp"];
  var refreshTime = chartConfig["Refreshtime"];
  var desiredDateStr;

  if (refreshTime == null) {
    refreshTime = "0000";
  }

  if (refreshDate == null) {
    desiredDateStr = appstr("Never");
  } else {
    desiredDateStr = getReadableStringFromDateAndTime(
      refreshDate,
      refreshTime,
      dateFormat,
      timeFormat,
      true
    );
  }

  return appstr("lastrefresh") + ": " + desiredDateStr;
};

export const ChartHasRecordDataLoaded = chartConfig => {
  const recordsCollectionSet =
    chartConfig["employeesCollection"] != null ||
    (chartConfig["positionsCollection"] != null && chartConfig["holdersCollection"] != null);
  return (
    chartConfig["headers"] != null &&
    (chartConfig["employeesCollection"] != null || chartConfig["positionsCollection"]) &&
    chartConfig["subchartsCollection"] != null
  );
};

export const GetNeedRefreshDiagnostics = chartConfig => {
  return {
    sync: {
      ...(chartConfig?.sync || {}),
      diagnostics: {
        ...((chartConfig?.sync && chartConfig.sync?.diagnostics) || {}),
        needsRefresh: true
      }
    }
  };
};
